import {
  Alert,
  Checkbox,
  DatePicker,
  FormField,
  KeyValue,
  NumberStepper,
  Select,
  Text,
  Textarea,
  WeightInput,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { errorCode, formErrors } from '../../../lib/errors';
import { useBatch, useBatches, useContainers, useRecordFilling } from '../api';
import { formatKg } from '../format';
import styles from './harvest.module.css';

export interface FillingDialogProps {
  open: boolean;
  onClose: () => void;
  /** Without batch the dialog asks for one (batches with bulk honey). */
  batchId?: string;
}

/** Fills bulk honey into jars, several container sizes at once (INV-010 – INV-013). */
export function FillingDialog({ open, onClose, batchId }: FillingDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const containers = useContainers(false, open);
  const batches = useBatches({}, open && !batchId);
  const [chosen, setChosen] = useState(batchId ?? '');
  const candidates = (batches.data ?? []).filter((b) => b.bulk_grams > 0);
  const batchValue = chosen || candidates[0]?.id || '';
  const batch = useBatch(batchValue, open);
  const record = useRecordFilling();
  const [filledOn, setFilledOn] = useState(todayIso());
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [loss, setLoss] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [overdraw, setOverdraw] = useState(false);
  const available = batch.data?.bulk_grams ?? 0;
  const lines = (containers.data ?? [])
    .map((c) => ({ container: c, count: counts[c.id] ?? 0 }))
    .filter((l) => l.count > 0);
  const filled = lines.reduce((sum, l) => sum + l.count * l.container.net_weight_grams, 0);
  const required = filled + (loss ?? 0);
  const missing = Math.max(0, required - available);
  const errors = formErrors(record.error, {
    lines: ['filling.lines', 'filling.duplicate'],
    date: ['filling.before'],
  });
  const insufficient = errorCode(record.error) === 'stock.insufficient_bulk';

  const close = () => {
    record.reset();
    onClose();
  };
  const submit = () =>
    record.mutate(
      {
        batchId: batchValue,
        body: {
          filled_on: filledOn,
          lines: lines.map((l) => ({ container_id: l.container.id, count: l.count })),
          loss_grams: loss ?? 0,
          notes: notes || null,
          allow_overdraw: overdraw,
        },
      },
      {
        onSuccess: (b) => {
          toast.show(
            t('harvest.filling.recorded', {
              count: lines.reduce((s, l) => s + l.count, 0),
              lot: b.lot,
            }),
          );
          close();
        },
      },
    );

  return (
    <FormDialog
      open={open}
      onClose={close}
      drawer
      title={t('harvest.filling.title')}
      description={
        batch.data
          ? t('harvest.filling.description', {
              lot: batch.data.lot,
              variety: batch.data.variety.name,
            })
          : undefined
      }
      submitLabel={t('harvest.filling.submit')}
      onSubmit={submit}
      pending={record.isPending}
      error={insufficient ? undefined : errors.general}
    >
      {!batchId && (
        <FormField label={t('harvest.filling.batch')} required>
          {(p) => (
            <Select
              {...p}
              value={batchValue}
              onChange={(e) => {
                setChosen(e.target.value);
                setOverdraw(false);
              }}
              options={candidates.map((b) => ({
                value: b.id,
                label: `${b.lot} · ${b.variety.name} · ${formatKg(b.bulk_grams)}`,
              }))}
            />
          )}
        </FormField>
      )}
      {!batchId && batches.data && candidates.length === 0 && (
        <Alert tone="info">{t('harvest.filling.noBulk')}</Alert>
      )}
      <FormField label={t('harvest.filling.filledOn')} required error={errors.field.date}>
        {(p) => <DatePicker {...p} value={filledOn} onChange={setFilledOn} max={todayIso()} />}
      </FormField>
      <fieldset className={styles.shares} aria-invalid={errors.field.lines ? true : undefined}>
        <legend>{t('harvest.filling.containers')}</legend>
        <ul className={styles.lineList}>
          {(containers.data ?? []).map((c) => (
            <li key={c.id} className={styles.lineRow}>
              <span className={styles.shareName}>{c.name}</span>
              <NumberStepper
                aria-label={t('harvest.filling.countFor', { container: c.name })}
                size="sm"
                value={counts[c.id] ?? null}
                onChange={(v) => setCounts((m) => ({ ...m, [c.id]: v }))}
                min={0}
                placeholder="0"
                unit={t('harvest.filling.jarsUnit')}
                labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
              />
              <Text as="span" size="xs" tone="muted">
                {formatKg((counts[c.id] ?? 0) * c.net_weight_grams)}
              </Text>
            </li>
          ))}
        </ul>
        {errors.field.lines && (
          <Text size="xs" tone="danger" role="alert">
            {errors.field.lines}
          </Text>
        )}
      </fieldset>
      <FormField label={t('harvest.filling.loss')}>
        {(p) => (
          <WeightInput
            {...p}
            value={loss}
            onChange={setLoss}
            stepKg={0.1}
            labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
          />
        )}
      </FormField>
      <div aria-live="polite">
        <KeyValue
          items={[
            { key: 'required', label: t('harvest.filling.required'), value: formatKg(required) },
            { key: 'available', label: t('harvest.filling.available'), value: formatKg(available) },
            {
              key: 'remaining',
              label: t('harvest.filling.remaining'),
              value: formatKg(Math.max(0, available - required)),
            },
          ]}
        />
      </div>
      {(missing > 0 || insufficient) && (
        <Alert tone="warning" title={t('harvest.filling.overTitle')}>
          <p style={{ margin: 0 }}>{t('harvest.filling.over', { missing: formatKg(missing) })}</p>
          <Checkbox
            label={t('harvest.filling.overConfirm', { missing: formatKg(missing) })}
            checked={overdraw}
            onChange={(e) => setOverdraw(e.target.checked)}
          />
        </Alert>
      )}
      <FormField label={t('harvest.filling.notes')}>
        {(p) => (
          <Textarea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
    </FormDialog>
  );
}
